"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";

// Transactional texts (confirmation, order ready, receipt) are included in every
// plan (lib/billing/entitlements.ts). Marketing SMS is a separate metered add-on —
// no per-message price is quoted here because none is published.

export default function SmsCostScroll() {
  const { t } = useLang();
  const [perDay, setPerDay] = useState(40);

  const perMonth = perDay * 30;

  return (
    <section className="relative py-32 px-6 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("What it costs", "Lo que cuesta")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Order texts. ", "SMS de pedidos. ")}
            <span className="text-redy-charcoal/30">{t("Included.", "Incluidos.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "Confirmation, order-ready and receipt texts come with every plan — no separate SMS vendor, no extra contract. Marketing texts to your list are a separate add-on, priced by use.",
              "Los SMS de confirmación, orden lista y recibo vienen en todos los planes — sin vendor de SMS aparte, sin contrato extra. Los SMS de marketing a tu lista son un add-on aparte, que se cobra por uso."
            )}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="bg-[#FAFAF8] border border-black/5 rounded-[32px] p-8 md:p-12 max-w-[820px] mx-auto"
        >
          {/* Slider */}
          <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-3">
            {t("Order texts per day", "SMS de pedidos por día")}
          </div>

          <div className="flex items-end justify-between mb-3">
            <div className="text-[56px] font-black text-redy-charcoal leading-none tabular-nums">
              {perDay}
            </div>
            <div className="text-[12px] font-bold text-redy-secondary mb-2">
              {t("SMS / day", "SMS / día")}
            </div>
          </div>

          <input
            type="range"
            min={5}
            max={300}
            step={5}
            value={perDay}
            onChange={(e) => setPerDay(Number(e.target.value))}
            className="w-full h-2 rounded-full appearance-none cursor-pointer bg-gradient-to-r from-[#F4722B] to-[#D7263D]/30 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-[3px] [&::-webkit-slider-thumb]:border-[#F4722B] [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:cursor-grab active:[&::-webkit-slider-thumb]:cursor-grabbing"
          />

          <div className="flex justify-between text-[11px] font-bold text-redy-secondary/50 mt-2">
            <span>5</span>
            <span>300+</span>
          </div>

          {/* That's = monthly */}
          <div className="mt-5 text-[13px] font-bold text-redy-charcoal">
            {t("That's", "Eso son")}{" "}
            <span className="tabular-nums" style={{ color: "#F4722B" }}>
              {perMonth.toLocaleString()}
            </span>{" "}
            {t("messages / month.", "mensajes / mes.")}
          </div>

          <div className="h-px bg-black/5 my-10" />

          {/* Included + volume side-by-side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div>
              <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-3">
                {t("You pay", "Tú pagas")}
              </div>
              <div className="text-[40px] md:text-[52px] font-black tracking-tight text-redy-charcoal leading-none tabular-nums">
                {t("Included", "Incluido")}
              </div>
              <div className="text-[12px] font-medium text-redy-secondary mt-2">
                {t("Order texts, in every plan", "SMS de pedidos, en todos los planes")}
              </div>
            </div>

            <div>
              <div className="text-[10px] font-black tracking-widest uppercase mb-3" style={{ color: "#D7263D" }}>
                {t("Without shouting", "Sin gritar")}
              </div>
              <div
                className="text-[40px] md:text-[52px] font-black tracking-tight leading-none tabular-nums"
                style={{
                  background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                {perMonth.toLocaleString()}
              </div>
              <div className="text-[12px] font-medium text-redy-secondary mt-2">
                {t("Order updates a month sent by text, not shouted across the room.", "Avisos de pedido al mes por SMS, no gritados en el salón.")}
              </div>
            </div>
          </div>

          <div
            className="rounded-[20px] px-6 py-5 text-white"
            style={{
              background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
              boxShadow: "0 15px 40px rgba(244,114,43,0.30)",
            }}
          >
            <div className="text-[11px] font-black tracking-widest uppercase text-white/75 mb-1">
              {t("Separate SMS vendor needed", "Vendor de SMS aparte")}
            </div>
            <div className="text-[40px] md:text-[48px] font-black tabular-nums leading-none">
              {t("None", "Ninguno")}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
